import React, {useEffect, useMemo, useState} from "react";
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import {
	ManagementRoleAssignment,
	ManagementRoleAssignmentList,
	ManagementRoleAssignmentRestControllerApiFactory,
	ManagementRoleAssignmentRoleEnum,
	Person,
} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {dateToString} from "../../../../components/utils/dates";
import {
	btnSx,
	EmptyState,
	fullName,
	ListCard,
	Page,
	PageHeader,
	Panel,
	PersonAvatar,
	SkeletonRows,
	SoftChip,
	tableContainerSx,
	tableSx,
	Tone,
	Toolbar,
} from "../../../../components/kit";
import {PersonPicker} from "../../employee/interview/form";
import {MANAGEMENT_ROLES, managementRoleLabel} from "../../../../components/layout/shared/management_roles";

type PersonRef = { id?: number, label: string };
type Row = ManagementRoleAssignmentList;

const today = () => dayjs().format("YYYY-MM-DD");

const refOf = (p?: { id?: number, name?: string, surnames?: string }): PersonRef | undefined =>
	p?.id ? {id: p.id, label: fullName(p)} : undefined;

const toPerson = (r?: PersonRef) => r?.id ? ({id: r.id} as Person) : undefined;

/** Vigente, cerrada o futura, por sus fechas. */
const status = (a: Row): { label: string, tone: Tone } => {
	const t = today();
	if (a.startDate > t) {
		return {label: "Futura", tone: "info"};
	}
	if (a.endDate && a.endDate < t) {
		return {label: "Cerrada", tone: "neutral"};
	}
	return {label: "Vigente", tone: "success"};
};

const period = (a: Row) => a.endDate
	? `Del ${dateToString(a.startDate)} al ${dateToString(a.endDate)}`
	: `Desde el ${dateToString(a.startDate)}`;

const PersonCell = ({p, empty}: { p?: { id?: number, name?: string, surnames?: string }, empty?: React.ReactNode }) => p?.id ? (
	<Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
		<PersonAvatar name={fullName(p)} size={28}/>
		<Typography variant="body2" fontWeight={600}>{fullName(p)}</Typography>
	</Stack>
) : <>{empty ?? <Typography variant="body2" color="text.secondary">—</Typography>}</>;

interface FormState {
	id?: number;
	role: string;
	holder?: PersonRef;
	deputy?: PersonRef;
	startDate: string;
	endDate?: string;
	approvedBy?: PersonRef;
	approvalDate?: string;
	notes?: string;
}

const formOf = (a?: Row, role?: string): FormState => a ? {
	id: a.id, role: a.role, holder: refOf(a.holder), deputy: refOf(a.deputy), startDate: a.startDate, endDate: a.endDate,
	approvedBy: refOf(a.approvedBy), approvalDate: a.approvalDate, notes: a.notes,
} : {role: role || "", startDate: today(), approvalDate: today()};

const errorOf = (e: any): string => e?.response?.data?.error || "No se ha podido guardar. Revisa los datos e inténtalo de nuevo.";

/**
 * Designaciones de los roles del sistema de gestión (#13896): quién tiene hoy cada rol, y el
 * historial. Las reglas (una vigente por rol, segregación seguridad/sistema, cierre de la anterior
 * al designar otra) las aplica el API y su mensaje se enseña tal cual en el diálogo.
 */
export const RoleAssignmentPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(ManagementRoleAssignmentRestControllerApiFactory);
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));

	const [all, setAll] = useState<Row[]>();
	const [roleFilter, setRoleFilter] = useState("");
	const [editing, setEditing] = useState<FormState>();
	const [closing, setClosing] = useState<Row>();
	const [closeDate, setCloseDate] = useState(today());
	const [error, setError] = useState<string>();
	const [saving, setSaving] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);

	const load = () => apiManager.execute(ctrl.listManagementRoleAssignment()).then(setAll).catch(() => setAll([]));
	useEffect(() => {
		load();
	}, []);

	const current = useMemo(() => {
		const t = today();
		return new Map((all || []).filter(a => a.startDate <= t && (!a.endDate || a.endDate >= t)).map(a => [a.role as string, a]));
	}, [all]);

	const history = useMemo(() => (all || [])
		.filter(a => !roleFilter || a.role === roleFilter)
		.sort((a, b) => b.startDate.localeCompare(a.startDate)), [all, roleFilter]);

	const open = (f: FormState) => {
		setError(undefined);
		setConfirmDelete(false);
		setEditing(f);
	};

	const save = () => {
		if (!editing) {
			return;
		}
		setSaving(true);
		setError(undefined);
		const bean = {
			id: editing.id, role: editing.role as ManagementRoleAssignmentRoleEnum,
			holder: toPerson(editing.holder), deputy: toPerson(editing.deputy), approvedBy: toPerson(editing.approvedBy),
			startDate: editing.startDate, endDate: editing.endDate || undefined,
			approvalDate: editing.approvalDate || undefined, notes: editing.notes || undefined,
		} as ManagementRoleAssignment;
		apiManager.executeRaw(ctrl.saveManagementRoleAssignment(bean), "Designación guardada", true)
			.then(() => {
				setEditing(undefined);
				return load();
			})
			.catch(e => setError(errorOf(e)))
			.finally(() => setSaving(false));
	};

	const remove = () => {
		if (!editing?.id) {
			return;
		}
		setSaving(true);
		apiManager.executeRaw(ctrl.deleteOrDismissManagementRoleAssignment(editing.id), "Designación eliminada", true)
			.then(() => {
				setEditing(undefined);
				return load();
			})
			.catch(e => setError(errorOf(e)))
			.finally(() => setSaving(false));
	};

	const close = () => {
		if (!closing?.id) {
			return;
		}
		setSaving(true);
		setError(undefined);
		apiManager.executeRaw(ctrl.closeManagementRoleAssignment(closing.id, closeDate), "Designación cerrada", true)
			.then(() => {
				setClosing(undefined);
				return load();
			})
			.catch(e => setError(errorOf(e)))
			.finally(() => setSaving(false));
	};

	const vacant = MANAGEMENT_ROLES.filter(r => !current.get(r.value)?.holder?.id).length;

	const currentActions = (role: string, a?: Row) => (
		<Stack direction="row" spacing={0.5} justifyContent={{md: "flex-end"}} sx={{whiteSpace: "nowrap"}} onClick={e => e.stopPropagation()}>
			{a && <Button size="small" sx={btnSx} onClick={() => open(formOf(a))}>Editar</Button>}
			{a && <Button size="small" sx={btnSx} color="inherit" onClick={() => {
				setError(undefined);
				setCloseDate(today());
				setClosing(a);
			}}>Cerrar</Button>}
			<Button size="small" variant="outlined" sx={btnSx} onClick={() => open(formOf(undefined, role))}>
				{a ? "Sustituir" : "Designar"}
			</Button>
		</Stack>
	);

	const holderCell = (a?: Row) => !a
		? <SoftChip tone="warning" label="Sin designar"/>
		: a.holder?.id ? <PersonCell p={a.holder}/> : <SoftChip tone="warning" label="Vacante"/>;

	return (
		<Page>
			<PageHeader title="Roles del sistema de gestión"
						subtitle="Quién tiene cada rol de ISO 9001, ISO/IEC 27001 y ENS, desde cuándo y quién lo aprobó."
						actions={<Button variant="contained" startIcon={<AddRoundedIcon/>} sx={btnSx}
										 onClick={() => open(formOf())}>Nueva designación</Button>}/>

			<Panel title="Vigentes hoy" icon={<VerifiedUserOutlinedIcon fontSize="small" color="action"/>}
				   subtitle={all ? (vacant === 0 ? "Todos los roles tienen titular." : `${vacant} de ${MANAGEMENT_ROLES.length} roles sin titular.`) : undefined}>
				{!all ? <SkeletonRows rows={4}/> : compact ? (
					<Stack spacing={1.5}>
						{MANAGEMENT_ROLES.map(r => {
							const a = current.get(r.value);
							return (
								<ListCard key={r.value}>
									<Typography variant="subtitle2" fontWeight={700}>{r.label}</Typography>
									<Box sx={{mt: 1}}>{holderCell(a)}</Box>
									{a?.deputy?.id && <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
										{a.holder?.id ? "Suplente" : "En funciones"}: {fullName(a.deputy)}
									</Typography>}
									{a && <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
										{period(a)}{a.approvedBy?.id ? ` · aprobada por ${fullName(a.approvedBy)}` : ""}
									</Typography>}
									<Box sx={{mt: 1.5}}>{currentActions(r.value, a)}</Box>
								</ListCard>
							);
						})}
					</Stack>
				) : (
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={tableSx}>
							<TableHead>
								<TableRow>
									<TableCell>Rol</TableCell>
									<TableCell>Titular</TableCell>
									<TableCell>Suplente</TableCell>
									<TableCell>Desde</TableCell>
									<TableCell>Aprobada por</TableCell>
									<TableCell/>
								</TableRow>
							</TableHead>
							<TableBody>
								{MANAGEMENT_ROLES.map(r => {
									const a = current.get(r.value);
									return (
										<TableRow key={r.value} hover>
											<TableCell sx={{fontWeight: 600}}>{r.label}</TableCell>
											<TableCell>{holderCell(a)}</TableCell>
											<TableCell>
												<PersonCell p={a?.deputy}/>
												{a && !a.holder?.id && a.deputy?.id &&
													<Typography variant="caption" color="text.secondary">En funciones</Typography>}
											</TableCell>
											<TableCell>{a ? dateToString(a.startDate) : "—"}</TableCell>
											<TableCell>
												{a?.approvedBy?.id ? <>
													<Typography variant="body2">{fullName(a.approvedBy)}</Typography>
													{a.approvalDate && <Typography variant="caption" color="text.secondary">{dateToString(a.approvalDate)}</Typography>}
												</> : <Typography variant="body2" color="text.secondary">—</Typography>}
											</TableCell>
											<TableCell align="right">{currentActions(r.value, a)}</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Panel>

			<Panel title="Historial" icon={<HistoryRoundedIcon fontSize="small" color="action"/>} sx={{mt: 3}}
				   subtitle="Todas las designaciones, vigentes y pasadas. Pulsa una para corregirla.">
				<Toolbar left={
					<TextField select size="small" label="Rol" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
							   SelectProps={{displayEmpty: true}} InputLabelProps={{shrink: true}}
							   sx={{minWidth: {xs: "100%", sm: 320}}}>
						<MenuItem value="">Todos ({all?.length ?? 0})</MenuItem>
						{MANAGEMENT_ROLES.map(r => (
							<MenuItem key={r.value} value={r.value}>{r.label} ({(all || []).filter(a => a.role === r.value).length})</MenuItem>
						))}
					</TextField>
				}/>
				{!all ? <SkeletonRows rows={3}/> : history.length === 0 ? (
					<EmptyState icon={<HistoryRoundedIcon/>} title="Sin designaciones"
								description="Pulsa «Nueva designación» para registrar la primera."/>
				) : compact ? (
					<Stack spacing={1.5}>
						{history.map(a => {
							const s = status(a);
							return (
								<ListCard key={a.id} onClick={() => open(formOf(a))}>
									<Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
										<Typography variant="subtitle2" fontWeight={700}>{managementRoleLabel(a.role)}</Typography>
										<SoftChip tone={s.tone} label={s.label}/>
									</Stack>
									<Typography variant="body2" sx={{mt: 0.75}}>
										{a.holder?.id ? fullName(a.holder) : "Vacante"}
										{a.deputy?.id ? ` · ${a.holder?.id ? "suplente" : "en funciones"} ${fullName(a.deputy)}` : ""}
									</Typography>
									<Typography variant="caption" color="text.secondary">{period(a)}</Typography>
								</ListCard>
							);
						})}
					</Stack>
				) : (
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={tableSx}>
							<TableHead>
								<TableRow>
									<TableCell>Rol</TableCell>
									<TableCell>Titular</TableCell>
									<TableCell>Suplente</TableCell>
									<TableCell>Periodo</TableCell>
									<TableCell>Estado</TableCell>
									<TableCell>Observaciones</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{history.map(a => {
									const s = status(a);
									return (
										<TableRow key={a.id} hover sx={{cursor: "pointer"}} onClick={() => open(formOf(a))}>
											<TableCell sx={{fontWeight: 600}}>{managementRoleLabel(a.role)}</TableCell>
											<TableCell><PersonCell p={a.holder} empty={<SoftChip tone="warning" label="Vacante"/>}/></TableCell>
											<TableCell><PersonCell p={a.deputy}/></TableCell>
											<TableCell sx={{whiteSpace: "nowrap"}}>{period(a)}</TableCell>
											<TableCell><SoftChip tone={s.tone} label={s.label}/></TableCell>
											<TableCell sx={{maxWidth: 280}}>
												<Typography variant="body2" color="text.secondary" noWrap>{a.notes || "—"}</Typography>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}
				<Stack direction="row" spacing={1} alignItems="flex-start" sx={{mt: 2, color: "text.secondary"}}>
					<InfoOutlinedIcon sx={{fontSize: 16, mt: 0.25}}/>
					<Typography variant="caption">
						Al designar a otra persona en un rol, la designación anterior se cierra sola el día antes. Un rol puede quedar
						vacante con un suplente que lo ejerce en funciones. El responsable de seguridad no puede ser el titular ni el
						suplente del responsable del sistema.
					</Typography>
				</Stack>
			</Panel>

			<Dialog open={!!editing} onClose={() => !saving && setEditing(undefined)} fullWidth maxWidth="sm"
					PaperProps={{sx: {borderRadius: 3}}}>
				{editing && (
					<>
						<DialogTitle sx={{fontWeight: 700}}>{editing.id ? "Editar designación" : "Nueva designación"}</DialogTitle>
						<DialogContent>
							<Stack spacing={2.5} sx={{pt: 1}}>
								{error && <Alert severity="error">{error}</Alert>}
								<TextField select label="Rol" value={editing.role} disabled={!!editing.id}
										   onChange={e => setEditing({...editing, role: e.target.value})}>
									{MANAGEMENT_ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
								</TextField>
								<PersonPicker label="Titular" value={editing.holder} onChange={v => setEditing({...editing, holder: v})}
											  helperText="Déjalo vacío si el rol queda vacante y lo ejerce en funciones el suplente."/>
								<PersonPicker label="Suplente" value={editing.deputy} onChange={v => setEditing({...editing, deputy: v})}/>
								<Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))"}, gap: 2}}>
									<TextField type="date" label="Inicio" value={editing.startDate || ""} InputLabelProps={{shrink: true}}
											   onChange={e => setEditing({...editing, startDate: e.target.value})}/>
									{editing.id && <TextField type="date" label="Fin" value={editing.endDate || ""} InputLabelProps={{shrink: true}}
															  helperText="Vacío mientras siga vigente."
															  onChange={e => setEditing({...editing, endDate: e.target.value})}/>}
								</Box>
								<Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))"}, gap: 2}}>
									<PersonPicker label="Aprobada por" value={editing.approvedBy} onChange={v => setEditing({...editing, approvedBy: v})}/>
									<TextField type="date" label="Fecha de aprobación" value={editing.approvalDate || ""} InputLabelProps={{shrink: true}}
											   onChange={e => setEditing({...editing, approvalDate: e.target.value})}/>
								</Box>
								<TextField label="Observaciones" multiline minRows={2} value={editing.notes || ""}
										   onChange={e => setEditing({...editing, notes: e.target.value})}/>
							</Stack>
						</DialogContent>
						<DialogActions sx={{px: 3, pb: 2.5, justifyContent: "space-between"}}>
							<Box>
								{editing.id && (confirmDelete
									? <Button color="error" variant="outlined" sx={btnSx} disabled={saving} onClick={remove}>Confirmar: eliminar del historial</Button>
									: <Button color="error" sx={btnSx} disabled={saving} onClick={() => setConfirmDelete(true)}>Eliminar</Button>)}
							</Box>
							<Stack direction="row" spacing={1}>
								<Button sx={btnSx} disabled={saving} onClick={() => setEditing(undefined)}>Cancelar</Button>
								<Button variant="contained" sx={btnSx} disabled={saving || !editing.role || !editing.startDate} onClick={save}>
									Guardar designación
								</Button>
							</Stack>
						</DialogActions>
					</>
				)}
			</Dialog>

			<Dialog open={!!closing} onClose={() => !saving && setClosing(undefined)} fullWidth maxWidth="xs"
					PaperProps={{sx: {borderRadius: 3}}}>
				{closing && (
					<>
						<DialogTitle sx={{fontWeight: 700}}>Cerrar designación</DialogTitle>
						<DialogContent>
							<Stack spacing={2} sx={{pt: 1}}>
								{error && <Alert severity="error">{error}</Alert>}
								<Typography variant="body2" color="text.secondary">
									{managementRoleLabel(closing.role)}: {closing.holder?.id ? fullName(closing.holder) : "vacante"}, {period(closing).toLowerCase()}.
									El rol quedará sin designar desde el día siguiente.
								</Typography>
								<TextField type="date" label="Último día en el rol" value={closeDate} InputLabelProps={{shrink: true}}
										   onChange={e => setCloseDate(e.target.value)}/>
							</Stack>
						</DialogContent>
						<DialogActions sx={{px: 3, pb: 2.5}}>
							<Button sx={btnSx} disabled={saving} onClick={() => setClosing(undefined)}>Cancelar</Button>
							<Button variant="contained" sx={btnSx} disabled={saving || !closeDate} onClick={close}>Cerrar designación</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</Page>
	);
};
